import React, { useState, useEffect, useRef } from 'react';
import { SEO } from '../components/SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import SectionHeader from '../components/SectionHeader';
import { useScrollAnimation, useCounter } from '../hooks/useAnimations';

export const Home = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);

  const yearsCount = useCounter(10, 2000, statsVisible);
  const clientsCount = useCounter(50, 2000, statsVisible);
  const sectorsCount = useCounter(7, 2000, statsVisible);
  const submissionsCount = useCounter(100, 2000, statsVisible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const services = [
    {
      title: 'FDA Regulatory Compliance',
      description: 'State-level FDA compliance support - manufacturing licences, blood centre licences, testing lab licences, retail/wholesale drug licences, and inspection preparedness.',
      link: '/services/fda',
    },
    {
      title: 'CDSCO Regulatory Compliance',
      description: 'New drug/device applications, NCE applications, FDC approvals, biological/biosimilar applications, medical device applications, and NOC applications.',
      link: '/services/cdsco',
    },
    {
      title: 'Global Regulatory Compliance',
      description: 'Strategic regulatory planning and end-to-end dossier support for multi-product, multi-market registrations across pharma, devices, cosmetics, and food.',
      link: '/services/global-regulatory',
    },
    {
      title: 'Revised Schedule M GMP Advisory',
      description: 'GMP gap assessments, SOP development, compliance roadmaps, and audit readiness aligned to the Revised Schedule M.',
      link: '/services/gmp',
    },
    {
      title: 'WHO GMP Certification Advisory',
      description: 'Expert guidance for WHO GMP certification, pre-qualification support, and international GMP compliance for pharmaceutical manufacturers.',
      link: '/services/who-gmp',
    },
    {
      title: 'Specialized Regulatory Consultation',
      description: 'Specialized regulatory consulting for medical devices, cosmetic, nutraceutical, and food products - registration, labeling, and claims substantiation.',
      link: '/services/specialized',
    },
    {
      title: 'Facility Design & Project Consultancy',
      description: 'Pharmaceutical-grade facility design, layout optimization, HVAC and cleanroom planning, and regulatory-compliant infrastructure consultancy.',
      link: '/services/facility-design',
    },
    {
      title: 'Turnkey Key Project',
      description: 'End-to-end project management for pharma facility setup - from concept and design through construction, equipment procurement, qualification, and operational handover.',
      link: '/services/turnkey',
    },
    {
      title: 'Global Drug Sourcing',
      description: 'Procurement of innovator, branded, and reference listed drugs (RLDs) for research, BA/BE studies, and clinical applications globally.',
      link: '/services/drug-sourcing',
    },
  ];

  const industries = [
    'Pharmaceuticals (Formulations, APIs, Generics)',
    'Medical Devices & Diagnostics',
    'Cosmetics & Personal Care',
    'Nutraceuticals & Dietary Supplements',
    'Food & Functional Food Industry',
    'Ayurveda & Herbal Products',
    'Contract Research & Manufacturing Organizations (CRO/CMO)',
  ];

  const whyPegasus = [
    {
      title: '10+ Years of Regulatory Excellence',
      description: 'Over a decade of hands-on experience navigating India and global regulatory frameworks, consistently delivering results for complex, high-stakes submissions.',
    },
    {
      title: 'Multidisciplinary Expert Team',
      description: 'Our team comprises pharmacists, engineers, quality management system specialists, and regulatory affairs professionals - bringing technical depth to every engagement.',
    },
    {
      title: 'End-to-End Service Portfolio',
      description: 'From regulatory strategy and dossier submissions to GMP readiness, facility design, turnkey execution, and global drug sourcing - one partner for the entire compliance lifecycle.',
    },
    {
      title: 'Proven Track Record with Reputed Clients',
      description: 'We have successfully partnered with leading national and multinational organizations including Lupin, 3M, L\'Oreal, ACG, Bilcare, Unichem, Hikal Ltd, and many more.',
    },
    {
      title: 'Faster Approvals, Reduced Risk',
      description: 'Our strategic, submission-ready approach minimizes query cycles, reduces time-to-approval, and ensures that compliance is never a bottleneck to your business growth.',
    },
    {
      title: 'Tailored Engagements',
      description: 'No two businesses are identical. We offer customized regulatory roadmaps, retainer-based consulting, and project-based engagements to suit every business model.',
    },
  ];

  const clients = [
    'Lupin', '3M', 'L\'Oreal', 'ACG', 'Baramati Agro', 'Schreiber Dynamix',
    'Atmasantulan - Santulan Ayurveda', 'Veko Care', 'Bilcare', 'Unichem',
    'Melzer', 'Honour Labs', 'Hikal Ltd', 'Genpep',
  ];

  const testimonials = [
    {
      quote: 'Pegasus brought unmatched clarity to our CDSCO submission process. Their team was proactive, professional, and delivered results faster than we expected.',
      author: 'Senior Manager, Regulatory Affairs',
      company: 'Leading Pharma Company',
    },
    {
      quote: 'Their GMP advisory services completely transformed our manufacturing facility compliance readiness. We passed the inspection without a single major observation.',
      author: 'QA Head',
      company: 'Mid-Sized API Manufacturer',
    },
    {
      quote: 'The global drug sourcing service saved us weeks of procurement time for our BA/BE study. Highly recommended.',
      author: 'Director',
      company: 'Clinical Research Organization',
    },
  ];

  return (
    <div className="pt-[72px]">
      <SEO page="home" />
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden"
        style={{ minHeight: 'calc(100vh - 72px)' }}
      >
        {/* Background image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_pharma-compliance-7/artifacts/sx2lkui8_image.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center right',
          }}
        />
        {/* Blue gradient overlay - strong on left, transparent on right */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to right, #1570EF 0%, #1570EFee 35%, #1570EFaa 55%, transparent 75%)',
          }}
        />

        <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6 flex items-center" style={{ minHeight: 'calc(100vh - 72px)' }}>
          <div className="max-w-2xl py-20">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-white" style={{ lineHeight: '1.2' }}>
              Pegasus Clinicare -<br />
              Your Trusted Partner in<br />
              Regulatory Excellence
            </h1>
            <p className="text-lg sm:text-xl mb-8 text-white/90 max-w-xl">
              We help pharma, medical device, cosmetic, and food companies get regulatory approvals faster. Simple guidance, clear compliance, quick market access.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="px-8 py-4 bg-white text-[#1570EF] font-semibold rounded-full hover:bg-blue-50 hover:scale-105 transition-all duration-200 text-center"
              >
                Get a Free Consultation
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="px-8 py-4 bg-transparent text-white font-semibold rounded-full border-2 border-white hover:bg-white hover:text-[#1570EF] transition-all duration-200 text-center"
              >
                Explore Our Services
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-white/80">
              <div className="flex items-center gap-2">
                <Check size={18} />
                <span>Trusted Since 2014</span>
              </div>
              <div className="flex items-center gap-2">
                <Check size={18} />
                <span>50+ Reputed Clients</span>
              </div>
              <div className="flex items-center gap-2">
                <Check size={18} />
                <span>Multi-Sector Expertise</span>
              </div>
              <div className="flex items-center gap-2">
                <Check size={18} />
                <span>India & Global Markets</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-24 bg-white" data-testid="who-we-are-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader
            title="Regulatory Clarity. Accelerated Approvals. Global Reach."
          />
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="fade-in">
              <p className="text-base sm:text-lg mb-6" style={{ color: '#1E293B' }}>
                Pegasus Clinicare Pvt. Ltd. is a trusted regulatory consulting and drug sourcing firm based in India. For over 10 years, we have helped pharma, medical device, cosmetic, and food companies get their products approved and reach the market.
              </p>
              <p className="text-base sm:text-lg mb-6" style={{ color: '#1E293B' }}>
                Since 2014, we have worked with startups, manufacturers, and MNCs to handle all regulatory needs - from CDSCO and FDA approvals to Revised Schedule M GMP compliance and global drug sourcing.
              </p>
              <p className="text-base sm:text-lg" style={{ color: '#1E293B' }}>
                Our hands-on approach means you get more than just paperwork. We give you clear guidance for faster approvals, lower compliance risk, and lasting market success.
              </p>
              <Link
                to="/about"
                data-testid="who-we-are-cta"
                className="inline-flex items-center gap-2 mt-6 text-[#1570EF] font-semibold hover:gap-3 transition-all duration-200"
              >
                Learn More About Us
                <ArrowRight size={20} />
              </Link>
            </div>
            <div className="fade-in">
              <img
                src="https://customer-assets.emergentagent.com/job_pharma-compliance-7/artifacts/56mgfek0_02%20regulatory%20calrity%20.jpg"
                alt="Regulatory professionals"
                className="rounded-2xl shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Snapshot */}
      <section className="py-24" style={{ backgroundColor: '#EFF6FF' }} data-testid="services-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader
            label="OUR SERVICES"
            title="Comprehensive Regulatory & Consulting Solutions"
            description="Expert-led services that move your products forward, faster."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-card-${index}`}
                className="bg-white p-8 rounded-2xl border border-[#BFDBFE] hover:border-[#1570EF] hover:shadow-xl transition-all duration-300 hover:-translate-y-1 fade-in"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <h3 className="text-xl font-semibold mb-3" style={{ color: '#1B3A6B' }}>
                  {service.title}
                </h3>
                <p className="text-sm mb-4" style={{ color: '#1E293B' }}>
                  {service.description}
                </p>
                <Link
                  to={service.link}
                  className="inline-flex items-center gap-2 text-[#1570EF] font-semibold text-sm hover:gap-3 transition-all duration-200"
                >
                  Learn More
                  <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link
              to="/services"
              data-testid="view-all-services-cta"
              className="inline-flex items-center gap-2 text-[#1570EF] font-semibold hover:gap-3 transition-all duration-200"
            >
              View All Services
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-24 bg-white" data-testid="industries-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader
            label="INDUSTRIES"
            title="Industries We Serve"
            description="Deep domain expertise across the following sectors:"
          />
          <div className="flex flex-wrap gap-3 justify-center max-w-4xl mx-auto">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="px-6 py-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-full text-sm font-medium fade-in"
                style={{ color: '#1B3A6B', transitionDelay: `${index * 0.05}s` }}
              >
                {industry}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Pegasus */}
      <section className="py-24" style={{ backgroundColor: '#EFF6FF' }} data-testid="why-pegasus-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader
            label="WHY CHOOSE US"
            title="Why Companies Trust Pegasus Clinicare"
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyPegasus.map((item, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl border border-[#BFDBFE] fade-in"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 bg-[#1570EF] rounded-full flex items-center justify-center mb-4">
                  <Check size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-bold mb-3" style={{ color: '#1B3A6B' }}>
                  {item.title}
                </h3>
                <p className="text-sm" style={{ color: '#1E293B' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Counter */}
      <section
        ref={statsRef}
        className="py-16 bg-[#1B3A6B] text-white"
        data-testid="stats-section"
      >
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center counter">
              <div className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: '#1570EF' }}>
                {yearsCount}+
              </div>
              <div className="text-sm sm:text-base">Years in Business</div>
            </div>
            <div className="text-center counter">
              <div className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: '#1570EF' }}>
                {clientsCount}+
              </div>
              <div className="text-sm sm:text-base">Clients Served</div>
            </div>
            <div className="text-center counter">
              <div className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: '#1570EF' }}>
                {sectorsCount}+
              </div>
              <div className="text-sm sm:text-base">Industry Sectors</div>
            </div>
            <div className="text-center counter">
              <div className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: '#1570EF' }}>
                {submissionsCount}+
              </div>
              <div className="text-sm sm:text-base">Successful Submissions</div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Logos */}
      <section className="py-16 bg-white overflow-hidden" data-testid="clients-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader
            label="OUR CLIENTS"
            title="Trusted by Industry Leaders"
          />
          <div className="logo-marquee">
            <div className="logo-marquee-content">
              {[...clients, ...clients].map((client, index) => (
                <div
                  key={index}
                  className="inline-block mx-8 px-6 py-4 border border-[#BFDBFE] rounded-lg"
                  style={{ minWidth: '150px' }}
                >
                  <div className="text-base font-semibold text-center" style={{ color: '#1B3A6B' }}>
                    {client}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-center mt-8">
            <Link
              to="/clients"
              data-testid="view-all-clients-cta"
              className="inline-flex items-center gap-2 text-[#1570EF] font-semibold hover:gap-3 transition-all duration-200"
            >
              View All Clients
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24" style={{ backgroundColor: '#EFF6FF' }} data-testid="testimonials-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <SectionHeader
            label="TESTIMONIALS"
            title="What Our Clients Say"
          />
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                data-testid={`testimonial-${index}`}
                className={`bg-white p-8 rounded-2xl border-2 transition-all duration-500 ${
                  activeTestimonial === index ? 'border-[#1570EF] shadow-xl' : 'border-[#BFDBFE]'
                }`}
              >
                <p className="text-base mb-6 italic" style={{ color: '#1E293B' }}>
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-[#BFDBFE] pt-4">
                  <div className="font-semibold" style={{ color: '#1B3A6B' }}>
                    {testimonial.author}
                  </div>
                  <div className="text-sm" style={{ color: '#0E7C86' }}>
                    {testimonial.company}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-[#1B3A6B] text-white" data-testid="cta-banner">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Navigate Regulatory Complexity with Confidence?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Connect with our regulatory experts today and let us design a compliance strategy built around your business goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="cta-banner-consultation"
              className="px-8 py-4 bg-[#1570EF] text-white font-semibold rounded-full hover:bg-[#1E40AF] hover:scale-105 transition-all duration-200"
            >
              Schedule a Free Consultation
            </Link>
            <a
              href="tel:+919850271210"
              data-testid="cta-banner-call"
              className="px-8 py-4 bg-white text-[#1B3A6B] font-semibold rounded-full border-2 border-white hover:bg-transparent hover:text-white transition-all duration-200"
            >
              Call Us: +91 9850271210
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
